import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FileShareResponse, FileShareVisibility } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { filesApi } from '@/services/files.api';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M5 5l14 14" />
      <path d="M19 5L5 19" />
    </svg>
  );
}

const EXPIRY_PRESETS = ['NEVER', 'HOUR', 'DAY', 'WEEK', 'CUSTOM'] as const;
type ExpiryPreset = (typeof EXPIRY_PRESETS)[number];

const PRESET_SECONDS: Record<Exclude<ExpiryPreset, 'CUSTOM'>, number | null> = {
  NEVER: null,
  HOUR: 3600,
  DAY: 86400,
  WEEK: 604800,
};

/**
 * Creates a share link for one file, and lists/revokes the ones that
 * already exist for it. Two independent axes, the same as the underlying
 * contract: who can open the link (anyone with it, or specific people from
 * the directory), and for how long (a preset duration, a custom date, or
 * forever) — a password is available either way.
 */
export function ShareDialog({ fileId, onClose }: { fileId: string; onClose: () => void }) {
  const { t, locale } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();

  const [visibility, setVisibility] = useState<FileShareVisibility>('ANYONE_WITH_LINK');
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [expiryPreset, setExpiryPreset] = useState<ExpiryPreset>('WEEK');
  const [customExpiry, setCustomExpiry] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipients, setRecipients] = useState<{ id: string; displayName: string }[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const shares = useQuery({
    queryKey: ['cloud', 'shares', fileId],
    queryFn: ({ signal }) => filesApi.listShares(fileId, signal),
  });

  const people = useQuery({
    queryKey: ['people', 'picker', recipientSearch],
    queryFn: ({ signal }) => peopleApi.list({ search: recipientSearch, limit: 8 }, signal),
    enabled: visibility === 'SPECIFIC_PEOPLE' && recipientSearch.trim().length >= 2,
  });

  const create = useMutation({
    mutationFn: () => {
      const expiresInSeconds =
        expiryPreset === 'CUSTOM'
          ? customExpiry
            ? Math.max(1, Math.round((new Date(customExpiry).getTime() - Date.now()) / 1000))
            : null
          : PRESET_SECONDS[expiryPreset];

      return filesApi.createShare(fileId, {
        visibility,
        password: requiresPassword && password ? password : null,
        expiresInSeconds,
        recipientPersonIds: visibility === 'SPECIFIC_PEOPLE' ? recipients.map((r) => r.id) : null,
      });
    },
    onSuccess: (share) => {
      setCreatedUrl(share.url);
      void queryClient.invalidateQueries({ queryKey: ['cloud', 'shares', fileId] });
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => filesApi.revokeShare(id),
    onSuccess: (_data, id) => {
      // Revoking deletes the share outright on the server — removed from
      // the cache directly so it drops out of the list this instant,
      // rather than waiting on `invalidateQueries`'s network round-trip.
      queryClient.setQueryData<FileShareResponse[]>(['cloud', 'shares', fileId], (current) =>
        current?.filter((share) => share.id !== id),
      );
      void queryClient.invalidateQueries({ queryKey: ['cloud', 'shares', fileId] });
    },
  });

  const candidates = (people.data?.data ?? []).filter(
    (person) => !recipients.some((r) => r.id === person.id) && !person.archivedAt,
  );

  const activeShares = shares.data ?? [];

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="share-form-title">
      <div className="dialog-body">
        <div className="dialog-header">
          <h2 id="share-form-title">{t('files.share.create')}</h2>
          <Button
            type="button"
            variant="ghost"
            className="btn-icon"
            aria-label={t('common.actions.close')}
            title={t('common.actions.close')}
            onClick={onClose}
          >
            <CloseIcon />
          </Button>
        </div>

        {formError && <Alert tone="danger">{formError}</Alert>}

        {createdUrl ? (
          <div className="inline-confirm">
            <p>{t('files.share.created')}</p>
            <Field
              label={t('files.share.link')}
              name="share-url"
              value={createdUrl}
              readOnly
              dir="ltr"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(createdUrl).then(() => setCopied(true));
              }}
            >
              {copied ? t('common.actions.copied') : t('common.actions.copy')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setCreatedUrl(null)}>
              {t('files.share.createAnother')}
            </Button>
          </div>
        ) : (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              setFormError(null);
              create.mutate();
            }}
          >
            <fieldset className="form-grid">
              <legend>{t('files.share.visibilityLabel')}</legend>
              <label>
                <input
                  type="radio"
                  name="visibility"
                  checked={visibility === 'ANYONE_WITH_LINK'}
                  onChange={() => setVisibility('ANYONE_WITH_LINK')}
                />
                {t('files.share.visibility.ANYONE_WITH_LINK')}
              </label>
              <label>
                <input
                  type="radio"
                  name="visibility"
                  checked={visibility === 'SPECIFIC_PEOPLE'}
                  onChange={() => setVisibility('SPECIFIC_PEOPLE')}
                />
                {t('files.share.visibility.SPECIFIC_PEOPLE')}
              </label>
            </fieldset>

            {visibility === 'SPECIFIC_PEOPLE' && (
              <div className="member-picker">
                <Field
                  label={t('files.share.addRecipient')}
                  name="recipient-search"
                  type="search"
                  value={recipientSearch}
                  onChange={(event) => setRecipientSearch(event.target.value)}
                  placeholder={t('groups.group.choosePerson')}
                />
                {candidates.length > 0 && (
                  <ul className="picker-results">
                    {candidates.map((person) => (
                      <li key={person.id}>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setRecipients([
                              ...recipients,
                              { id: person.id, displayName: person.displayName },
                            ]);
                            setRecipientSearch('');
                          }}
                        >
                          {person.displayName}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                {recipients.length > 0 && (
                  <ul className="chip-list">
                    {recipients.map((recipient) => (
                      <li key={recipient.id} className="chip">
                        {recipient.displayName}
                        <button
                          type="button"
                          onClick={() =>
                            setRecipients(recipients.filter((r) => r.id !== recipient.id))
                          }
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="field">
              <label className="field-label" htmlFor="field-expiry">
                {t('files.share.expiresAfter')}
              </label>
              <select
                id="field-expiry"
                className="field-input"
                value={expiryPreset}
                onChange={(event) => setExpiryPreset(event.target.value as ExpiryPreset)}
              >
                {EXPIRY_PRESETS.map((preset) => (
                  <option key={preset} value={preset}>
                    {t(`files.share.expiry.${preset}`)}
                  </option>
                ))}
              </select>
            </div>

            {expiryPreset === 'CUSTOM' && (
              <Field
                label={t('files.share.customExpiry')}
                name="customExpiry"
                type="datetime-local"
                value={customExpiry}
                onChange={(event) => setCustomExpiry(event.target.value)}
                dir="ltr"
              />
            )}

            <label className="field-checkbox">
              <input
                type="checkbox"
                checked={requiresPassword}
                onChange={(event) => setRequiresPassword(event.target.checked)}
              />
              {t('files.share.requiresPassword')}
            </label>
            {requiresPassword && (
              <Field
                label={t('files.share.password')}
                name="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                dir="ltr"
              />
            )}

            <footer className="dialog-actions">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={create.isPending}
                disabled={
                  (visibility === 'SPECIFIC_PEOPLE' && recipients.length === 0) ||
                  (requiresPassword && !password)
                }
              >
                {t('files.share.create')}
              </Button>
            </footer>
          </form>
        )}

        {activeShares.length > 0 && (
          <section className="settings-card">
            <h3 className="settings-card-title">{t('files.share.existing')}</h3>
            <ul className="chip-list">
              {activeShares.map((share) => (
                <li key={share.id} className="detail-row">
                  <span>
                    {t(`files.share.visibility.${share.visibility}`)}
                    {share.expiresAt && ` · ${formatDateTime(share.expiresAt, locale)}`}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm(t('files.share.confirmRevoke'))) {
                        revoke.mutate(share.id);
                      }
                    }}
                  >
                    {t('files.share.revoke')}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </dialog>
  );
}
